import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type {
  CreateSupportTicketFormData,
  SupportTicketCreateApiResponse,
} from "../supportTicketTypes";
import { parseSupportTicketApiError } from "../utils/parseSupportTicketApiError";
import { buildCreateSupportTicketPayload } from "../utils/buildCreateSupportTicketPayload";

/**
 * Creates a new support ticket for the current organization.
 *
 * @param orgId - Authorized organization id for the current user.
 * @param form - Create-ticket modal form values.
 * @returns Created ticket detail from the API.
 */
export const createSupportTicket = async (
  orgId: string,
  form: CreateSupportTicketFormData,
): Promise<SupportTicketCreateApiResponse> => {
  const body = buildCreateSupportTicketPayload(orgId, form);

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.CREATE_SUPPORT_TICKET, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        await parseSupportTicketApiError(
          response,
          "Failed to create support ticket",
        ),
      );
    }

    const result = (await response.json()) as SupportTicketCreateApiResponse;
    logDataToConsole("createSupportTicket", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in createSupportTicket");
    throw error;
  }
};
